import { getTableIntentLink } from "@components/drillDown/DrillDown.utils";
import { GroupHeader } from "@components/formGroup/GroupHeader";
import {
    formatMinutesToHoursString,
    formatMinutesToTimeFormat,
    parseTimeUserInput
} from "@components/inputs/date/utils";
import { SwitchType } from "@components/inputs/switch/Switch";
import SimpleTable from "@components/simpleTable";
import {
    getInfoValue,
    getSimpleBoolSelectItems,
    ifAll,
    ifAny,
    IFieldDefFn,
    IGetValueArgs,
    not,
    TGetValueFn
} from "@components/smart/FieldInfo";
import { ActionButtonPosition } from "@components/smart/smartFastEntryList";
import { FilterBarGroup, getDefaultFilterGroupDef } from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IFormGroupDef } from "@components/smart/smartFormGroup/SmartFormGroup";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { IColumn, IRow } from "@components/table";
import { getRouteByDocumentType } from "@odata/EntityTypes";
import {
    CurrencyEntity,
    DocumentEntity,
    DocumentTypeEntity,
    EntitySetName,
    EntityTypeEntity,
    EntityTypeName,
    FileMetadataEntity,
    IDocumentEntity,
    IPrPayslipEmploymentEntity,
    IPrPayslipEmploymentExtraSalaryEntity,
    IPrPayslipEntity,
    IPrPayslipItemEntity,
    IPrPayslipLiabilityReceivableEntity,
    IPrPayslipMinimumHealthInsuranceEntity,
    IPrSalaryAdvanceEntity,
    PrAttendanceEntity,
    PrDeductionEntity,
    PrEmployeeEntity,
    PrEmployeeTemporalEntity,
    PrEmploymentEntity,
    PrEmploymentRetirementSavingEntity,
    PrEmploymentTemporalEntity,
    PrEmploymentTypeEntity,
    PrExtraSalaryComputationTypeEntity,
    PrExtraSalaryTypeEntity,
    PrPayslipBalancingPeriodEntity,
    PrPayslipDeductionEntity,
    PrPayslipEmploymentBenefitEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEmploymentExtraSalaryEntity,
    PrPayslipEmploymentRetirementSavingEntity,
    PrPayslipEmploymentSalaryComponentEntity,
    PrPayslipEntity,
    PrPayslipItemEntity,
    PrPayslipLiabilityReceivableEntity,
    PrPayslipMasterDataChangeEntity,
    PrPayslipMinimumHealthInsuranceEntity,
    PrPayslipVacationSummaryEntity,
    PrRetirementSavingsCompanyEntity,
    PrRetirementSavingsProductEntity,
    PrSalaryComponentTemporalEntity,
    PrWorkingPatternEntity
} from "@odata/GeneratedEntityTypes";
import {
    DocumentTypeCode,
    PrAttendanceStatusCode,
    PrDeductionCalculationTypeCode,
    PrExtraSalaryComputationTypeCode,
    PrExtraSalaryTypeCode,
    PrMinimumHealthInsuranceTypeCode,
    PrPayrollPeriodStatusCode,
    PrPayslipItemTypeCode,
    PrSalaryComponentAverageInclusionCode,
    PrSalaryComponentTypeCode,
    PrSocialInsuranceDiscountIneligibilityReasonCode
} from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { IFormatOptions } from "@odata/OData.utils";
import { setDefByEntityType } from "@pages/getDefByEntityType";
import {
    areAllBenefitsOfSameType,
    BenefitEmployeeTotalPath,
    BenefitEmployerTotalPath,
    BenefitTotalPath,
    getBenefitFieldDefinition,
    getMealVoucherShareUnitFormatter,
    isOtherBenefit
} from "@pages/payroll/benefits/Benefit.utils";
import {
    employeeNameFormatter,
    getEmployeeFilterDef,
    getEmployeeSummaryItemDef,
    getEmployeeTableColumnDefs
} from "@pages/payroll/employee/Employee.utils";
import { isLegislativeExtraSalary, isLegislativeExtraSalaryItem, } from "@pages/payroll/employment/Employment.utils";
import {
    ExtraSalaryAmountPath,
    getExtraSalaryFieldDefinition,
    getExtraSalaryValueUnit,
    isPartOfLegislativeExtraSalaryItem
} from "@pages/payroll/extraSalary/ExtraSalary.utils";
import LegislativeExtraSalariesSmartFastEntryList
    from "@pages/payroll/extraSalary/LegislativeExtraSalariesSmartFastEntryList";
import {
    getPayrollCurrencyFieldDef,
    getPayrollCurrencyTableFormatter,
    getPayrollCurrencyUnit
} from "@pages/payroll/payroll.utils";
import DeductionsEntryList from "@pages/payroll/payslip/DeductionsEntryList";
import { ExtraSalariesOverviewTable } from "@pages/payroll/payslip/ExtraSalariesOverviewTable";
import AttendanceOverview from "@pages/payroll/payslip/overview/AttendanceOverview";
import BalancingPeriodOverview from "@pages/payroll/payslip/overview/BalancingPeriodOverview";
import TotalOverview from "@pages/payroll/payslip/overview/TotalOverview";
import { isStartOfEndOfMonth } from "@pages/payroll/payslip/overview/TotalOverview.def";
import {
    getTotalOverviewAdditionalProperties,
    getTotalOverviewEmploymentAdditionalProperties
} from "@pages/payroll/payslip/overview/TotalOverview.utils";
import {
    AttendanceOverviewProps,
    currentEmploymentPath,
    DialogType,
    getPayslipCurrencyPath,
    hasBalancingPeriodOverview,
    hasOpenAttendance,
    hasUnseizableOverridePath,
    isCustomPayslipDeduction,
    isDelayedIncome,
    isPayslipEmploymentVisible,
    productPath,
    renderPayslipEmploymentTabs,
    tabsPath,
    UnseizableTotalAmount,
    validityRangeFormatter
} from "@pages/payroll/payslip/Payslip.utils";
import PayslipFormView, {
    IPrPayslipFormStorageCustomData,
    SocialInsuranceDiscountTypeLocalPath
} from "@pages/payroll/payslip/PayslipFormView";
import {
    areAllRetirementsOfSameType,
    getRetirementSavingsBasicFields,
    getRetirementSavingsComputationTypeDef,
    getRetirementSavingsIsIncludedAutomaticallyDef,
    getRetirementSavingsSecondaryFields
} from "@pages/payroll/retirementSavingsCompany/RetirementSavings.utils";
import {
    areAllSalaryComponentsOfSameType,
    getSalaryComponentFieldDefinition,
    getSalaryEnumNamespaceNames,
    SalaryComponentVariant
} from "@pages/payroll/salaryComponent/SalaryComponent.utils";
import { StatusCodeEnumTranslations } from "@pages/reports/CommonDefs";
import { isDefined, roundToDecimalPlaces } from "@utils/general";
import i18next from "i18next";
import { reduce } from "lodash";
import React from "react";
import { isFormReadOnly } from "views/formView/Form.utils";

import { DASH_CHARACTER, EMPTY_DASH } from "../../../constants";
import {
    BasicInputSizes,
    FastEntryInputSizes,
    FieldType,
    LabelStatus,
    Sort,
    TextAlign,
    ValidatorType
} from "../../../enums";
import { ColoredText } from "../../../global.style";
import { TValue } from "../../../global.types";
import { Model } from "../../../model/Model";
import BindingContext, { createPath, IEntity } from "../../../odata/BindingContext";
import { ROUTE_SALARY_ADVANCES } from "../../../routes";
import DateType, { DateFormat, DisplayFormat } from "../../../types/Date";
import NumberType from "../../../types/Number";
import { IFormDef } from "../../../views/formView/Form";
import { FormStorage } from "../../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../../views/table/TableView.utils";
import { getItemBreadCrumbsText, IGetDefinition, TFieldDefinition } from "../../PageUtils";

export const attendanceOverviewPath = BindingContext.localContext("attendanceOverview");
export const balancingPeriodOverviewPath = BindingContext.localContext("balancingPeriodOverview");
export const deductionsPath = BindingContext.localContext("deductions");
export const calculationsOverviewPath = BindingContext.localContext("calculationsOverview");

export const employmentTypesPath = BindingContext.localContext("employmentTypes");
export const attendancePath = BindingContext.localContext("attendance");

export const LegislativeExtraSalaryLocalPath = BindingContext.localContext("LegislativeExtraSalaries");
export const extraSalariesOverviewTablePath = BindingContext.localContext("extraSalariesOverviewTable");
export const MinimumHealthInsuranceTypeOverviewTablePath = BindingContext.localContext("minimumHealthInsuranceTypeOverviewTable");

export const salaryComponentsGroupId = "SalaryComponents";
export const extraSalariesGroupId = "ExtraSalaries";
export const BenefitsGroupId = "Benefits";
export const BalancingPeriodGroupId = "BalancingPeriod";
export const RetirementsGroupId = "Retirements";


const BenefitTotalFieldPaths = [
    BenefitEmployeeTotalPath, BenefitEmployerTotalPath, BenefitTotalPath
];

const isNotOneTimeSalaryComponent: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentSalaryComponentEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

const isNotOneTimeExtraSalary: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentExtraSalaryEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

const isNotOneTimeBenefit: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParent().navigate(PrPayslipEmploymentBenefitEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

const isNotOneTimeRetirementSavings: TGetValueFn<boolean> = (args) => {
    const oneTimeBc = args.bindingContext.getParentCollection().navigate(PrPayslipEmploymentRetirementSavingEntity.IsOneTime);
    return !args.storage.getValue(oneTimeBc);
};

function isOnlyRealIncomeMinimumHealthInsurance(args: IGetValueArgs) {
    const minHealthInsuranceItems = args.storage.getValueByPath(PrPayslipEntity.MinimumHealthInsurance);
    return minHealthInsuranceItems?.every((item: IPrPayslipMinimumHealthInsuranceEntity) => item.PrMinimumHealthInsuranceTypeCode === PrMinimumHealthInsuranceTypeCode.RealIncome);
}

const isOvertimeLegislativeExtraSalary: TGetValueFn<boolean> = (args) => {
    const parentBc = args.bindingContext.getParent();
    if (!parentBc.getKey()) {
        // label in smart fast entry list -> always visible
        return true;
    }
    const typeCodeBc = parentBc
        .navigate(PrPayslipEmploymentExtraSalaryEntity.Type)
        .navigate(PrExtraSalaryTypeEntity.Code);
    const typeCode = args.storage.getValue(typeCodeBc);
    return typeCode === PrExtraSalaryTypeCode.Overtime;
};

const hasLiabilitiesOrReceivables: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.LiabilitiesAndReceivables?.length > 0;
};

const hasSalaryAdvances: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.Items?.some(item => item.Type?.Code === PrPayslipItemTypeCode.SalaryAdvance);
};

const hasRetirementSavings: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.Employments.some(e => e.RetirementSavings?.length > 0);
};


function formatValidityPeriod(from: Date, to: Date): string {
    return `${DateType.format(from, DateFormat.dayAndMonth)} - ${DateType.format(to, DateFormat.dayAndMonth)}`;
}

export enum PayslipFormAction {
    ConnectLiabilities = "ConnectLiabilities",
    ConnectReceivables = "ConnectReceivables",
    SendPayslipEmail = "SendPayslipEmail",
    PastCorrections = "PastCorrections"
}

const workedHoursFormatter: TFormatterFn = (_, args: IFormatOptions<IPrPayslipEntity>) => {
    const worked = reduce(args.entity.Employments, (acc, e) => {
        return acc + e.WorkedIncludingHolidays;
    }, 0);
    return formatMinutesToHoursString(worked);
};

const getDeductionTypeCode = (args: IGetValueArgs): PrDeductionCalculationTypeCode => {
    const parentBc = args.bindingContext.getParent();
    return args.storage.getValue(parentBc.navigate(PrPayslipDeductionEntity.CalculationTypeCode)) as PrDeductionCalculationTypeCode;
};

const hasCustomDeduction = (args: IGetValueArgs): boolean => {
    const deductions = args.storage.getValueByPath(PrPayslipEntity.Deductions);
    return deductions?.some(isCustomPayslipDeduction);
};

const hasDeductions = (args: IGetValueArgs): boolean => {
    const { Items } = args.storage.data.entity as IPrPayslipEntity;
    return !!Items?.find(i => i?.TypeCode === PrPayslipItemTypeCode.DeductionTotal);
};

const hasOwnUnseizableAmount = (args: IGetValueArgs): boolean => {
    const val = args.storage.getValueByPath(hasUnseizableOverridePath);
    return val === UnseizableTotalAmount.Own;
};

// parser for fields that store time as minutes in backend and use time picker as input
export const timeStampToMinutesParser = (val: string): number => {
    const parsed = parseTimeUserInput(val, { expectSeparator: true });
    if (parsed) {
        const idx = parsed.length - 2;
        const [h, m] = [parsed.substring(0, idx), parsed.substring(idx)].map(Number);
        return h * 60 + m;
    }
    return NaN;
};

// formatter for fields that store time as minutes in backend and use time picker as input
export const timeStampToMinutesFormatter = (val: TValue): string => {
    const minutes = Number(val);
    if (!val || isNaN(minutes)) {
        return val?.toString();
    }
    return formatMinutesToTimeFormat(minutes);
};

export function getPayslipEmploymentColumnDefinition(): TFieldDefinition {
    return {
        label: i18next.t("Payslip:Table.EmploymentsType"),
        fieldSettings: {
            disableSort: true
        },
        additionalProperties: [{
            id: `${createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Employment, PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name)}`
        }],
        formatter: (_, args: IFormatOptions<IPrPayslipEntity>) => {
            if (args.entity.Employments.length === 1) {
                return args.entity.Employments[0]?.Employment?.Type?.Name;
            }
            return i18next.t("Payslip:Table.MultipleEmployments").toString();
        }
    };
}

/**
 * Statutory compensation type has attendance with flag "IsStatutoryCompensation".
 * All employments have to be of same type, so we can check only the first one.
 * @param args
 */
const isStatutoryCompensationType: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    const attendance = payslip.Employments?.[0]?.Attendance;
    return !!attendance.IsStatutoryCompensation;
};

const isProcessedPayrollPeriod: TGetValueFn<boolean> = (args) => {
    const { storage } = args;
    const { currentPeriod } = storage.getCustomData() as IPrPayslipFormStorageCustomData;
    return [PrPayrollPeriodStatusCode.ProcessedAndClosed, PrPayrollPeriodStatusCode.Processed]
        .includes(currentPeriod?.StatusCode);
};

interface IGetDefinitionArgs {
    isOverview?: boolean;
}

const hasHealthInsuranceSurcharge: TGetValueFn<boolean> = (args) => {
    const payslip = args.storage.data.entity as IPrPayslipEntity;
    return payslip?.Items?.some(item => item.Type?.Code === PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployee
        || item.Type?.Code === PrPayslipItemTypeCode.HealthInsuranceMinimumSurchargePaidByEmployer);
};

export const getDefinitionsFactory: ((args?: IGetDefinitionArgs) => IGetDefinition) = (args) => {

    const isCustomerOverview = args?.isOverview;

    const getDefinitions: IGetDefinition = (context) => {
        const table: ISplitPageTableDef = {
            id: `${EntityTypeName.PrPayslip}Table`,
            additionalProperties: [
                { id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Attendance, PrAttendanceEntity.StatusCode) },
                { id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Employment, PrEmploymentEntity.TypeCode) },
                { id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Employment, PrEmploymentEntity.NumberOurs) },
                { id: createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.IsSmallScaleEmployment) },
                { id: PrPayslipEntity.MasterDataChanges },
                // data for Sending Payslip action
                { id: createPath(PrPayslipEntity.FileMetadata, FileMetadataEntity.Id) },
                { id: createPath(PrPayslipEntity.Employee, PrEmployeeEntity.Email) },
            ],
            preventStoreVariant: isCustomerOverview,
            filterBarDef: [{
                ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
                isValueHelp: true,
                defaultFilters: [
                    PrPayslipEntity.Employee,
                    PrPayslipEntity.AggregatedGrossSalary
                ],
                filterDefinition: {
                    ...getEmployeeFilterDef(PrPayslipEntity.Employee),
                    [PrPayslipEntity.AggregatedGrossSalary]: {
                        formatter: getPayrollCurrencyTableFormatter()
                    },
                }
            }],
            columns: [
                createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs),
                PrPayslipEntity.Employee,
                employmentTypesPath,
                attendancePath,
                createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.WorkedIncludingHolidays),
                PrPayslipEntity.AggregatedGrossSalary,
                PrPayslipEntity.DateLastSentByEmail
            ],
            columnDefinition: {
                ...getEmployeeTableColumnDefs(PrPayslipEntity.Employee),
                [employmentTypesPath]: getPayslipEmploymentColumnDefinition(),
                [PrPayslipEntity.DateLastSentByEmail]: {
                    label: i18next.t("Payslip:Table.DateLastSentByEmail"),
                    formatter: (value: TValue) => {
                        // const Icon = value ? StatusYesIcon : null;
                        // const tooltip = value ? DateType.format(value as Date, DisplayFormat.DateMedium) : EMPTY_DASH;
                        // return {
                        //     tooltip,
                        //     value: Icon ? (<Icon title={tooltip} height={IconSize.S} width={IconSize.S}/>) : EMPTY_DASH
                        // };
                        return value ? DateType.format(value as Date, DisplayFormat.DateMedium) : EMPTY_DASH;
                    },
                },
                [attendancePath]: {
                    label: i18next.t("Payslip:Table.Attendance"),
                    fieldSettings: {
                        disableSort: true
                    },
                    additionalProperties: [{
                        id: `${createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Attendance, PrAttendanceEntity.StatusCode)}`
                    }],
                    formatter: (_, args: IFormatOptions<IPrPayslipEntity>) => {
                        if (args.entity.Employments?.some(e => e.Attendance.StatusCode === PrAttendanceStatusCode.ToProcess)) {
                            return {
                                value: <ColoredText
                                    color={"C_SEM_text_bad"}>{i18next.t("Payslip:Table.AttendanceNotClosed")}</ColoredText>,
                                tooltip: i18next.t("Payslip:Table.AttendanceNotClosed")
                            };
                        }
                        if (args.entity.Employments?.some(e => [PrAttendanceStatusCode.Processed, PrAttendanceStatusCode.Locked].includes(e.Attendance.StatusCode))) {
                            return {
                                value: <ColoredText
                                    color={"C_SEM_text_good"}>{i18next.t("Payslip:Table.AttendanceClosed")}</ColoredText>,
                                tooltip: i18next.t("Payslip:Table.AttendanceClosed")
                            };
                        }
                        return EMPTY_DASH;
                    },
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.WorkedIncludingHolidays)]: {
                    label: i18next.t("Payslip:Table.Worked"),
                    formatter: workedHoursFormatter
                },
                [PrPayslipEntity.AggregatedGrossSalary]: {
                    formatter: getPayrollCurrencyTableFormatter()
                }
            },
            title: i18next.t(isCustomerOverview ? "Payslip:Table.TitleOverview" : "Payslip:Table.Title"),
            initialSortBy: [{ id: createPath(PrPayslipEntity.Employee, PrEmployeeEntity.NumberOurs), sort: Sort.Desc }]
        };

        const employmentsBaseDef: Partial<IFormGroupDef> = {
            collection: PrPayslipEntity.Employments,
            collectionOrder: PrPayslipEmploymentEntity.DateCreated,
            isVisible: ifAll(isPayslipEmploymentVisible, not(hasOpenAttendance)),
        };
        const { isVisible, ...employmentsBaseWithoutVisibilityDef } = employmentsBaseDef;

        const {
            label,
            ...SalaryComponentPercentageDef
        } = getSalaryComponentFieldDefinition(PrSalaryComponentTemporalEntity.Percentage);
        const AmountEmployeeDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.AmountEmployee);
        const AmountEmployerDef = getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.AmountEmployer);

        const SC_TypeDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Type, undefined, SalaryComponentVariant.Payslip);
        const SC_RegularSalaryTypeDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType, undefined, SalaryComponentVariant.Payslip);
        const SC_TimeBasedBonusDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType);
        const SC_AverageInclusionDef = getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.AverageInclusion);

        const calculationGroupDef = {
            id: "calculations",
            isVisible: not(hasOpenAttendance),
            rows: [[{ id: calculationsOverviewPath }]]
        };

        const form: IFormDef = {
            id: `${EntityTypeName.PrPayslip}Form`,
            title: i18next.t("Payslip:Form.Title"),
            isDeletable: isCustomerOverview ? false : (args) => {
                return !!args.storage.getValueByPath(PrPayslipEntity.IsDelayedIncome);
            },
            getItemBreadCrumbText: (storage: Model) => {
                const employee = (storage.data.entity as IPrPayslipEntity).Employee;
                return getItemBreadCrumbsText(storage, null, employeeNameFormatter(null, { item: employee }));
            },
            translationFiles: getDefinitions.translationFiles,
            ignoreVariants: true,
            isReadOnly: isCustomerOverview ? true : isProcessedPayrollPeriod,
            summary: [
                getEmployeeSummaryItemDef(PrPayslipEntity.Employee)
            ],
            files: {
                singlePreview: true,
                property: PrPayslipEntity.FileMetadata
            },
            customHeaderActions: isCustomerOverview ? [] : [
                {
                    id: PayslipFormAction.SendPayslipEmail,
                    label: i18next.t("Payslip:SendPayslipDialog.Title"),
                    iconName: "Send",
                    isDisabled: (args: IGetValueArgs) => {
                        const payslip = args.storage.data.entity as IPrPayslipEntity;
                        // no file metadata => nothing to be sent, disable the action
                        return !payslip?.FileMetadata?.Id;
                    },
                }, {
                    id: PayslipFormAction.ConnectLiabilities,
                    label: i18next.t("Payslip:Form.ConnectWithLiabilities"),
                    iconName: "ConnectLiability",
                    isDisabled: isProcessedPayrollPeriod,
                }, {
                    id: PayslipFormAction.ConnectReceivables,
                    label: i18next.t("Payslip:Form.ConnectWithReceivables"),
                    iconName: "ConnectReceivable",
                    isDisabled: isProcessedPayrollPeriod,
                },
                /* TODO: re-enable when past corrections are implemented // https://solitea-cz.atlassian.net/browse/DEV-42905
                {
                    id: PayslipFormAction.PastCorrections,
                    label: i18next.t("Payslip:Form.PastCorrections"),
                    iconName: "PastCorrections"
                }*/
            ],
            additionalProperties: [
                {
                    id: PrPayslipEntity.MasterDataChanges,
                    additionalProperties: [{
                        id: PrPayslipMasterDataChangeEntity.EntityId
                    }, {
                        id: PrPayslipMasterDataChangeEntity.EntityDisplayName
                    }, {
                        id: PrPayslipMasterDataChangeEntity.EntityType,
                        additionalProperties: [{
                            id: EntityTypeEntity.Name
                        }]
                    }, {
                        id: PrPayslipMasterDataChangeEntity.EntityTypeCode
                    }, {
                        id: PrPayslipMasterDataChangeEntity.CollectionItemChangeTypeCode
                    }, {
                        id: PrPayslipMasterDataChangeEntity.CollectionName
                    }, {
                        id: PrPayslipMasterDataChangeEntity.CollectionItemDisplayName
                    }, {
                        id: PrPayslipMasterDataChangeEntity.CollectionItemId
                    }]
                },
                { id: PrPayslipEntity.Month },
                { id: PrPayslipEntity.Year },
                { id: PrPayslipEntity.IsDelayedIncome },
                {
                    id: PrPayslipEntity.Employee,
                    additionalProperties: [
                        { id: PrEmployeeEntity.FirstName },
                    { id: PrEmployeeEntity.Email }, // send payslip
                        { id: PrEmployeeEntity.LastName },
                        {
                            id: PrEmployeeEntity.SalaryCurrency,
                            additionalProperties: [
                                { id: CurrencyEntity.Code },
                                { id: CurrencyEntity.MinorUnit },
                                { id: CurrencyEntity.Name },
                            ]
                        }
                    ]
                },
                { id: PrPayslipEntity.IsDeduceMinimumHealthInsurance },
                {
                id: PrPayslipEntity.MinimumHealthInsurance,
                additionalProperties: [{
                    id: PrPayslipMinimumHealthInsuranceEntity.PrMinimumHealthInsuranceTypeCode
                }, {
                    id: PrPayslipMinimumHealthInsuranceEntity.DateFrom
                }, {
                    id: PrPayslipMinimumHealthInsuranceEntity.DateTo
                }]
            },
                { id: PrPayslipEntity.IsMinimumHealthInsurancePayedByEmployer },
                {
                    id: PrPayslipEntity.Employments,
                    additionalProperties: [
                        {
                            id: PrPayslipEmploymentEntity.RetirementSavings,
                            additionalProperties: [
                                { id: PrPayslipEmploymentRetirementSavingEntity.IsOneTime },
                                { id: PrPayslipEmploymentRetirementSavingEntity.Amount },
                                { id: PrPayslipEmploymentRetirementSavingEntity.Percentage },
                                { id: PrPayslipEmploymentRetirementSavingEntity.SymbolVariable },
                                { id: PrPayslipEmploymentRetirementSavingEntity.SymbolSpecific },
                                { id: PrPayslipEmploymentRetirementSavingEntity.SymbolConstant },
                                { id: PrPayslipEmploymentRetirementSavingEntity.RemittanceInformation },
                            ]
                        },
                        { id: PrPayslipEmploymentEntity.AverageSalary /* for AverageSalaryOverride default */ },
                        { id: PrPayslipEmploymentEntity.IsSmallScaleEmployment },
                        {
                            id: PrPayslipEmploymentEntity.Attendance,
                            additionalProperties: [
                                { id: PrAttendanceEntity.StatusCode },
                                { id: PrAttendanceEntity.IsStatutoryCompensation },
                                { id: PrAttendanceEntity.Balance }
                            ]
                        },
                        {
                            id: PrPayslipEmploymentEntity.Employment,
                            additionalProperties: [
                                { id: PrEmploymentEntity.NumberOurs },
                                { id: PrEmploymentEntity.TypeCode },
                                { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Code) },
                                { id: createPath(PrEmploymentEntity.Type, PrEmploymentTypeEntity.Name) },
                                {
                                    id: PrEmploymentEntity.TemporalPropertyBag,
                                    additionalProperties: [
                                        { id: PrEmploymentTemporalEntity.DateValidFrom },
                                        { id: PrEmploymentTemporalEntity.DateValidTo },
                                        {
                                            id: PrEmploymentTemporalEntity.WorkingPattern,
                                            additionalProperties: [
                                                { id: PrWorkingPatternEntity.WorkingMinutesWeekly }
                                            ]
                                        }
                                    ]
                                },
                            ]
                        },
                        ...AttendanceOverviewProps.map(item => ({ id: item.id })),
                        ...getTotalOverviewEmploymentAdditionalProperties(),
                        {
                            id: PrPayslipEmploymentEntity.BalancingPeriods,
                            additionalProperties: [
                                { id: PrPayslipBalancingPeriodEntity.DateStart },
                                { id: PrPayslipBalancingPeriodEntity.DateEnd },
                                { id: PrPayslipBalancingPeriodEntity.PeriodLengthInWeeks },
                                { id: PrPayslipBalancingPeriodEntity.TotalFundInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.RemainingInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.ThisMonthWorkedInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.ThisMonthPlannedInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.ThisMonthBalanceInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.WholePeriodWorkedInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.WholePeriodPlannedInMinutes },
                                { id: PrPayslipBalancingPeriodEntity.WholePeriodBalanceInMinutes },
                            ]
                        },
                        { id: createPath(PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.IsOneTime) },
                        {
                            id: PrPayslipEmploymentEntity.SalaryComponents,
                            additionalProperties: [
                                { id: PrPayslipEmploymentSalaryComponentEntity.IsOneTime },
                                { id: PrPayslipEmploymentSalaryComponentEntity.DateValidFrom },
                                { id: PrPayslipEmploymentSalaryComponentEntity.DateValidTo },
                            ]
                        },
                        { id: createPath(PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.IsOneTime) }
                    ],
                },
                ...getTotalOverviewAdditionalProperties(),
                {
                    id: PrPayslipEntity.LiabilitiesAndReceivables,
                    additionalProperties: [
                        { id: PrPayslipLiabilityReceivableEntity.TransactionAmount },
                        { id: createPath(PrPayslipLiabilityReceivableEntity.TransactionCurrency, CurrencyEntity.Code) },
                    ]
                },
                { id: PrPayslipEntity.Deductions },
                { id: PrPayslipEntity.UnseizableTotalAmount },
                { id: PrPayslipEntity.IsUnreducedMultipleDistrainment },
            ],
            formControl: PayslipFormView,
            fieldDefinition: {
                [calculationsOverviewPath]: {
                    type: FieldType.Custom,
                    render: (args) => {
                        return <TotalOverview storage={args.storage as FormStorage<IPrPayslipEntity>}/>;
                    }
                },
                [createPath(PrPayslipEntity.Employments, attendanceOverviewPath)]: {
                    type: FieldType.Custom,
                    isVisible: ifAny(hasOpenAttendance, not(isStatutoryCompensationType)),
                    render: ({ storage, props }) => {
                        const bc = storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
                        const currentTabId = storage.getValueByPath(currentEmploymentPath);
                        const employmentBc = bc.addKey(currentTabId);
                        return <AttendanceOverview storage={storage as FormStorage<IPrPayslipEntity>}
                                                   onAttendanceChange={() => {
                                                       props.onChange({ bindingContext: employmentBc.navigate(PrPayslipEmploymentEntity.Attendance) });
                                                   }}
                                                   employmentBindingContext={employmentBc}/>;
                    }
                },
                [createPath(PrPayslipEntity.Employments, balancingPeriodOverviewPath)]: {
                    type: FieldType.Custom,
                    render: ({ storage }) => {
                        const bc = storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
                        const currentTabId = storage.getValueByPath(currentEmploymentPath);
                        return <BalancingPeriodOverview storage={storage as FormStorage<IPrPayslipEntity>}
                                                        employmentBindingContext={bc.addKey(currentTabId)}/>;
                    }
                },
                [tabsPath]: {
                    type: FieldType.Custom,
                    isVisible: (args: IGetValueArgs) => args.storage.getValueByPath(PrPayslipEntity.Employments)?.length > 1,
                    render: (args) => {
                        const employments: IPrPayslipEmploymentEntity[] = args.storage.getValueByPath(PrPayslipEntity.Employments);
                        return renderPayslipEmploymentTabs(employments, args.storage as FormStorage<IPrPayslipEntity>);
                    }
                },
                // ---------- LiabilitiesAndReceivables fields ----------
                [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Amount)]: {
                    label: i18next.t("Payslip:Form.LinkClearing"),
                },
                [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.Amount)]: {
                    label: i18next.t("Payslip:Form.LinkAmount"),
                },
                [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.DocumentType)]: {
                    label: i18next.t("Payslip:Form.LinkType"),
                    isRequired: false,
                    fieldSettings: {
                        displayName: DocumentTypeEntity.Name
                    }
                },
                [createPath(PrPayslipEntity.LiabilitiesAndReceivables, PrPayslipLiabilityReceivableEntity.Document)]: {
                    label: i18next.t("Payslip:Form.LinkDocument"),
                    fieldSettings: {
                        displayName: DocumentEntity.NumberOurs
                    },
                    formatter: (_, args: IFormatOptions<IPrPayslipEntity, IPrPayslipLiabilityReceivableEntity>) => {
                        const { item, storage } = args;
                        const { NumberOurs, DocumentType, Id } = item.Document;
                        return getTableIntentLink(NumberOurs as string, {
                            route: `${getRouteByDocumentType(DocumentType?.Code as DocumentTypeCode)}/${Id}`,
                            context: storage.context,
                            storage: storage
                        });
                    },
                },
                // ---------- /LiabilitiesAndReceivables fields ----------
                // ---------- Advanced Salary fields ----------
                [createPath(PrPayslipEntity.Items, PrPayslipItemEntity.SalaryAdvance)]: {
                    isReadOnly: true,
                    useForValidation: false,
                    label: i18next.t("Payslip:SalaryAdvanceList.Advance"),
                    formatter: (_, args: IFormatOptions<IPrPayslipEntity, IPrPayslipItemEntity>) => {
                        const { item, storage } = args;
                        const numberOurs = (item as IPrSalaryAdvanceEntity).NumberOurs;
                        if (!numberOurs) {
                            return DASH_CHARACTER;
                        }
                        return getTableIntentLink(numberOurs, {
                            route: `${ROUTE_SALARY_ADVANCES}/${item.Id}`,
                            context: storage.context,
                            storage
                        });
                    }
                },
                [createPath(PrPayslipEntity.Items, PrPayslipItemEntity.DecimalValue)]: {
                    isReadOnly: true,
                    useForValidation: false,
                    label: i18next.t("Payslip:SalaryAdvanceList.Amount"),
                    ...getPayrollCurrencyFieldDef()
                },
                [createPath(PrPayslipEntity.Items, PrPayslipItemEntity.Document, DocumentEntity.DocumentType)]: {
                    type: FieldType.ComboBox,
                    isReadOnly: true,
                    useForValidation: false,
                    label: i18next.t("Payslip:SalaryAdvanceList.PairedDocumentType"),
                    fieldSettings: {
                        displayName: "Name"
                    }
                },
                [createPath(PrPayslipEntity.Items, PrPayslipItemEntity.Document)]: {
                    isReadOnly: true,
                    useForValidation: false,
                    label: i18next.t("Payslip:SalaryAdvanceList.Document"),
                    additionalProperties: [{
                        id: DocumentEntity.NumberOurs
                    }],
                    formatter: (_, args: IFormatOptions<IPrPayslipEntity, IPrPayslipItemEntity>) => {
                        const { item, storage } = args;
                        const document = item as IDocumentEntity;
                        const route = getRouteByDocumentType(document.DocumentTypeCode);
                        const numberOurs = document.NumberOurs;
                        if (!numberOurs) {
                            return DASH_CHARACTER;
                        }
                        return getTableIntentLink(numberOurs, {
                            route: `${route}/${document.Id}`,
                            context: storage.context,
                            storage
                        });
                    }
                },
                [createPath(PrPayslipEntity.Items, PrPayslipItemEntity.Document, DocumentEntity.ClearedStatus)]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Payslip:SalaryAdvanceList.ClearedStatus"),
                    isReadOnly: true,
                    useForValidation: false,
                    fieldSettings: {
                        displayName: "Name"
                    }
                },
                // ---------- /Advanced Salary fields ----------
                // ---------- Deduction fields ----------
                [deductionsPath]: {
                    type: FieldType.Custom, // todo: refactor to standard component (ala "fast entry table")
                    label: i18next.t("Payslip:Form.Deductions"),
                    labelStatus: LabelStatus.Removed,
                    isVisible: hasCustomDeduction,
                    render: ({ props, storage, events }: IFieldDefFn) => {
                        return (<DeductionsEntryList storage={storage as FormStorage} {...props} {...events} />);
                    },
                    additionalProperties: [
                        { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Name) },
                        { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Amount) },
                        { id: createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.Deduction, PrDeductionEntity.TypeCode) },
                    ],
                    customizationData: {
                        useForCustomization: false
                    }
                },
                [createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.AmountOverride)]: {
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    width: BasicInputSizes.S,
                    isRequired: true,
                    fieldSettings: {
                        unit: (args: IGetValueArgs): string => {
                            if (getDeductionTypeCode(args) !== PrDeductionCalculationTypeCode.Fixed) {
                                return "%";
                            }
                            return getPayrollCurrencyUnit(getPayslipCurrencyPath())(args);
                        },
                    },
                    useForValidation: true
                },
                [createPath(PrPayslipEntity.Deductions, PrPayslipDeductionEntity.CalculationTypeCode)]: {
                    isReadOnly: true,
                    isVisible: (args: IGetValueArgs) => getDeductionTypeCode(args) !== PrDeductionCalculationTypeCode.Fixed,
                    fieldSettings: {
                        formatter: (val: TValue) => {
                            return i18next.t(`Payslip:Form.DeductionType_${val}`);
                        }
                    }
                },
                [PrPayslipEntity.IsUnreducedMultipleDistrainmentOverride]: {
                    type: FieldType.ComboBox,
                    isRequired: true,
                    isVisible: hasDeductions,
                    fieldSettings: {
                        items: getSimpleBoolSelectItems(),
                    }
                },
                [hasUnseizableOverridePath]: {
                    type: FieldType.ComboBox,
                    label: i18next.t("Payslip:Deductions.HasUnseizableOverride"),
                    isVisible: hasDeductions,
                    isRequired: true,
                    fieldSettings: {
                        items: [UnseizableTotalAmount.Calculated, UnseizableTotalAmount.Own].map(v => ({
                            id: v,
                            label: i18next.t(`Payslip:Deductions.HasUnseizableOverride_${v}`),
                        }))
                    },
                    affectedFields: [
                        { id: PrPayslipEntity.UnseizableTotalAmountOverride }
                    ]
                },
                [PrPayslipEntity.UnseizableTotalAmountOverride]: {
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    label: i18next.t("Payslip:Deductions.Value"),
                    isRequired: true,
                    isVisible: hasOwnUnseizableAmount,
                    validator: {
                        type: ValidatorType.Number,
                        settings: {
                            min: 0
                        }
                    },
                },
                // ---------- /Deduction fields ----------
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.VacationSummary)]: {
                    type: FieldType.Custom,
                    render: (args: IFieldDefFn) => {
                        // todo: move this to a separate component
                        const bc = args.storage.data.bindingContext.navigate(PrPayslipEntity.Employments);
                        const currentTabId = args.storage.getValueByPath(currentEmploymentPath);
                        const employmentBc = bc.addKey(currentTabId);
                        const currentEmp = args.storage.getValue(employmentBc) as IPrPayslipEmploymentEntity;
                        const vacationSummary = currentEmp.VacationSummary;

                        if (!vacationSummary) {
                            return null;
                        }

                        const columns: IColumn[] = [{
                            id: "name",
                            label: i18next.t("Payslip:VacationOverview.Name"),
                            textAlign: TextAlign.Left
                        }, {
                            id: "used",
                            label: i18next.t("Payslip:VacationOverview.Used"),
                            textAlign: TextAlign.Right
                        }, {
                            id: "entitlement",
                            label: i18next.t("Payslip:VacationOverview.Entitlement"),
                            textAlign: TextAlign.Right
                        }, {
                            id: "carriedOver",
                            label: i18next.t("Payslip:VacationOverview.CarriedOver"),
                            textAlign: TextAlign.Right
                        }, {
                            id: "remaining",
                            label: i18next.t("Payslip:VacationOverview.Remaining"),
                            textAlign: TextAlign.Right
                        }, {
                            id: "realEntitlement",
                            label: i18next.t("Payslip:VacationOverview.RealEntitlement"),
                            textAlign: TextAlign.Right
                        }];

                        function formatValue(minutes: number): string {
                            if (!isDefined(minutes)) {
                                return DASH_CHARACTER;
                            }
                            if (!vacationSummary.MinutesPerDay) {
                                return formatMinutesToHoursString(minutes);
                            }
                            const days = roundToDecimalPlaces(1, (minutes / vacationSummary.MinutesPerDay));
                            return `${formatMinutesToHoursString(minutes)} (${NumberType.format(days)} ${i18next.t("Common:General.Day")})`;
                        }

                        function formatSickDayValue(days: number): string {
                            if (!isDefined(days)) {
                                return DASH_CHARACTER;
                            }
                            return `${NumberType.format(days)} ${i18next.t("Common:General.Day")}`;
                        }

                        const rows: IRow[] = [{
                            id: "vacation",
                            values: {
                                name: i18next.t("Payslip:VacationOverview.Vacation").toString(),
                                used: formatValue(vacationSummary.VacationUsed),
                                entitlement: formatValue(vacationSummary.VacationEntitlement),
                                carriedOver: formatValue(vacationSummary.VacationCarriedOverFromLastYear),
                                remaining: formatValue(vacationSummary.VacationRemaining),
                                realEntitlement: formatValue(vacationSummary.VacationRealEntitlement)
                            }
                        }, {
                            id: "sickDay",
                            values: {
                                name: i18next.t("Payslip:VacationOverview.SickDays").toString(),
                                used: formatSickDayValue(vacationSummary.SickDaysUsed),
                                entitlement: formatSickDayValue(vacationSummary.SickDaysEntitlement),
                                carriedOver: DASH_CHARACTER,
                                remaining: formatSickDayValue(vacationSummary.SickDaysRemaining),
                                realEntitlement: DASH_CHARACTER
                            }
                        }];
                        return <SimpleTable columns={columns} rows={rows} showHeaderBorder={false}/>;
                    },
                    additionalProperties: [{
                        id: PrPayslipVacationSummaryEntity.MinutesPerDay
                    }, {
                        id: PrPayslipVacationSummaryEntity.SickDaysEntitlement
                    }, {
                        id: PrPayslipVacationSummaryEntity.SickDaysRemaining
                    }, {
                        id: PrPayslipVacationSummaryEntity.SickDaysUsed
                    }, {
                        id: PrPayslipVacationSummaryEntity.SickDaysUsedThisMonth
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationCarriedOverFromLastYear
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationEntitlement
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationRealEntitlement
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationRemaining
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationUsed
                    }, {
                        id: PrPayslipVacationSummaryEntity.VacationUsedThisMonth
                    }]
                },
                // --------- HealthInsuranceBase fields ----------
                [MinimumHealthInsuranceTypeOverviewTablePath]: {
                    type: FieldType.Custom,
                    render: (args: IFieldDefFn) => {
                        const healthInsItems = args.storage.getValueByPath(PrPayslipEntity.MinimumHealthInsurance);
                        if (!healthInsItems?.length) {
                            return null;
                        }
                        let columns: IColumn[] = [{
                            id: "type",
                            label: i18next.t("Payslip:Form.PrMinimumHealthInsuranceType") + ":",
                            textAlign: TextAlign.Left
                        }, {
                            id: "validity",
                            label: i18next.t("Payslip:FormOverviews.Validity") + ":",
                            textAlign: TextAlign.Right
                        }];

                        if (healthInsItems.length === 1) {
                            columns = columns.filter(col => col.id !== "validity");
                        }
                        const rows: IRow[] = healthInsItems.sort((a: IPrPayslipMinimumHealthInsuranceEntity, b: IPrPayslipMinimumHealthInsuranceEntity) => a.DateFrom?.getTime() - b.DateFrom?.getTime())
                            .map((item: IPrPayslipMinimumHealthInsuranceEntity) => ({
                                id: item.PrMinimumHealthInsuranceTypeCode,
                                values: {
                                    type: getEnumDisplayValue(EntityTypeName.PrMinimumHealthInsuranceType, item.PrMinimumHealthInsuranceTypeCode),
                                    validity: validityRangeFormatter(item.DateFrom, item.DateTo)
                                }
                            }));
                        return <SimpleTable columns={columns} rows={rows} showHeaderBorder={false}/>;
                    },
                    fieldSettings: {
                        additionalProperties: [],
                    },
                },
                [PrPayslipEntity.IsDeduceMinimumHealthInsuranceOverride]: {
                    type: FieldType.Switch,
                    label: i18next.t("Payslip:Form.IsDeduceMinimumHealthInsurance"),
                    defaultValue: true,
                    isVisible: not(isOnlyRealIncomeMinimumHealthInsurance),
                    fieldSettings: {
                        type: SwitchType.YesNo,
                    },
                    affectedFields: [{ id: PrPayslipEntity.IsMinimumHealthInsurancePayedByEmployerOverride }]
                },
                [PrPayslipEntity.IsMinimumHealthInsurancePayedByEmployerOverride]: {
                    type: FieldType.Switch,
                    label: i18next.t("Payslip:Form.IsMinimumHealthInsurancePayedByEmployer"),
                    defaultValue: false,
                    isVisible: ifAll(not(isOnlyRealIncomeMinimumHealthInsurance), (args: IGetValueArgs) => {
                        return !!args.storage.getValueByPath(PrPayslipEntity.IsDeduceMinimumHealthInsuranceOverride);
                    }),
                    fieldSettings: {
                        type: SwitchType.YesNo
                    }
                },
                // --------- /HealthInsuranceBase fields ----------
                // ---------- Social Insurance discount ----------
                [PrPayslipEntity.HasEmployerSocialInsuranceDiscountOverride]: {
                    type: FieldType.Switch,
                    labelStatus: LabelStatus.Removed,
                    isVisible: (args: IGetValueArgs) => {
                        const payslip = args.storage.data.entity as IPrPayslipEntity;
                        return payslip.SocialInsuranceDiscountIneligibilityReasonCode !== PrSocialInsuranceDiscountIneligibilityReasonCode.DiscountReasonIsNotSetOnEmployee;
                    },
                    tooltipAfter: (args) => {
                        const payslip = args.storage.data.entity as IPrPayslipEntity;
                        if (!payslip.SocialInsuranceDiscountIneligibilityReasonCode) {
                            return null;
                        }
                        return getEnumDisplayValue(EntityTypeName.PrSocialInsuranceDiscountIneligibilityReason, payslip.SocialInsuranceDiscountIneligibilityReasonCode);
                    },
                    isDisabled: (args: IGetValueArgs) => {
                        // if we know ineligibility reason, user cannot redefine to true.
                        return args.storage.data.entity.SocialInsuranceDiscountIneligibilityReasonCode !== null;
                    },
                    fieldSettings: {
                        type: SwitchType.YesNo
                    },
                    additionalProperties: [
                        { id: `/${PrPayslipEntity.SocialInsuranceDiscountIneligibilityReasonCode}` },
                        { id: `/${createPath(PrPayslipEntity.Employee, PrEmployeeEntity.TemporalPropertyBag, PrEmployeeTemporalEntity.PrSocialInsuranceDiscountTypeCode)}` },
                        { id: `/${createPath(PrPayslipEntity.Employee, PrEmployeeEntity.TemporalPropertyBag, PrEmployeeTemporalEntity.DateValidFrom)}` },
                        { id: `/${createPath(PrPayslipEntity.Employee, PrEmployeeEntity.TemporalPropertyBag, PrEmployeeTemporalEntity.DateValidTo)}` },
                    ],
                    affectedFields: [{ id: SocialInsuranceDiscountTypeLocalPath }]
                },
                [SocialInsuranceDiscountTypeLocalPath]: {
                    label: i18next.t("Payslip:Form.SocialInsuranceDiscountType"),
                    isReadOnly: true,
                    isVisible: (args) => !!args.storage.getValueByPath(PrPayslipEntity.HasEmployerSocialInsuranceDiscountOverride)
                },
                // ---------- /Social Insurance discount ----------
                // ---------- SalaryComponent fields ----------
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Name)]: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Name),
                    isReadOnly: isNotOneTimeSalaryComponent,
                    formatter: (val, args): string => {
                        const sc = args.storage.getValue(args.bindingContext.getParent());
                        if (sc?.IsOneTime || (isStartOfEndOfMonth(sc.DateValidFrom, "ValidFrom") && isStartOfEndOfMonth(sc.DateValidTo, "ValidTo"))) {
                            return val as string;
                        }
                        // add shortened validity to name
                        return `${val} | ${formatValidityPeriod(sc.DateValidFrom, sc.DateValidTo)}`;
                    }
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.Type)]: {
                    ...SC_TypeDef,
                    fieldSettings: {
                        ...SC_TypeDef.fieldSettings,
                        itemsForRender: (items, args: IGetValueArgs) => {
                            if (isDelayedIncome(args)) {
                                return items.filter(sc => sc.id !== PrSalaryComponentTypeCode.Regular);
                            }
                            return items;
                        }
                    },
                    isReadOnly: isNotOneTimeSalaryComponent, // todo: isNotNewItem? ...
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType)]: {
                    ...SC_RegularSalaryTypeDef,
                    isReadOnly: isNotOneTimeSalaryComponent, // todo: isNotNewItem? ...
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.IsBasicSalary)]: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.IsBasicSalary),
                    isReadOnly: isNotOneTimeSalaryComponent
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact)]: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact),
                    isReadOnly: isNotOneTimeSalaryComponent
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact)]: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact),
                    isReadOnly: isNotOneTimeSalaryComponent
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact)]: {
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact),
                    isReadOnly: isNotOneTimeSalaryComponent
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType)]: {
                    ...SC_TimeBasedBonusDef,
                    isReadOnly: isNotOneTimeSalaryComponent,
                    isVisible: ifAll(SC_TimeBasedBonusDef.isVisible, not(isStatutoryCompensationType), not(isDelayedIncome)),
                    isRequired: ifAny(SC_AverageInclusionDef.isRequired, isDelayedIncome), // https://solitea-cz.atlassian.net/browse/DEV-42826 hack to ensure field is sent even if not visible (for delayed income, we want to send Exclude value)
                    clearIfInvisible: not(isDelayedIncome),

                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.AverageInclusion)]: {
                    ...SC_AverageInclusionDef,
                    isReadOnly: isNotOneTimeSalaryComponent,
                    isVisible: ifAll(SC_AverageInclusionDef.isVisible, not(isStatutoryCompensationType), not(isDelayedIncome)),
                    clearIfInvisible: not(isDelayedIncome),
                    isRequired: ifAny(SC_AverageInclusionDef.isRequired, isDelayedIncome),
                    defaultValue: (args: IGetValueArgs) => {
                        // https://solitea-cz.atlassian.net/browse/DEV-42826 hack to ensure field is sent even if not visible (for delayed income, we want to send Exclude value)
                        return isDelayedIncome(args) ? PrSalaryComponentAverageInclusionCode.Exclude : getInfoValue(SC_AverageInclusionDef, "defaultValue", args);
                    }
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.PercentageOverride)]: {
                    ...SalaryComponentPercentageDef,
                    width: FastEntryInputSizes.XS
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.SalaryComponents, PrPayslipEmploymentSalaryComponentEntity.AmountOverride)]: {
                    label: i18next.t("Payslip:Form.AmountOverride"),
                    ...getSalaryComponentFieldDefinition(PrPayslipEmploymentSalaryComponentEntity.Amount)
                },
                // ---------- /SalaryComponent fields ----------
                // ---------- ExtraSalary fields ----------
                [createPath(PrPayslipEntity.Employments, extraSalariesOverviewTablePath)]: {
                    type: FieldType.Custom,
                    isVisible: not(isStatutoryCompensationType),
                    render: ({ storage }) => {
                        return <ExtraSalariesOverviewTable storage={storage as FormStorage}/>;
                    }
                },
                [createPath(PrPayslipEntity.Employments, LegislativeExtraSalaryLocalPath)]: {
                    type: FieldType.Custom,
                    label: i18next.t("Payslip:Form.LegislativeExtraSalaries"),
                    isVisible: not(isStatutoryCompensationType),
                    labelStatus: LabelStatus.Removed,
                    render: ({ props, storage }: IFieldDefFn) => {
                        const bc = props.parentProps.bindingContext
                            .getParent()
                            .navigate(PrEmploymentEntity.ExtraSalaries);
                        const formStorage = (storage as FormStorage);
                        const { definition } = formStorage.data;
                        const groupDef = definition.groups.find(grp => grp.id === extraSalariesGroupId);
                        const columns = groupDef.lineItems.columns.filter(col => col.id !== PrPayslipEmploymentExtraSalaryEntity.ComputationType);
                        const showOwnHeader = !formStorage.isReadOnly || storage.getValue(bc)?.find((es: IPrPayslipEmploymentExtraSalaryEntity) => !isLegislativeExtraSalaryItem(es));
                        return (<>
                            <LegislativeExtraSalariesSmartFastEntryList {...props}
                                                                        groupDefinitionId={extraSalariesGroupId}
                                                                        extraSalariesBindingContext={bc}
                                                                        columns={columns}
                                                                        isCollapsible={false}/>
                            {showOwnHeader &&
                                <GroupHeader title={i18next.t("Payslip:Form.OwnExtraSalaries")}></GroupHeader>}
                        </>);
                    },
                    customizationData: {
                        useForCustomization: false
                    }
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.Name)]: {
                    width: BasicInputSizes.L,
                    label: i18next.t("Payslip:Form.ExtraSalaryType"),
                    isReadOnly: ifAny(isNotOneTimeExtraSalary, isPartOfLegislativeExtraSalaryItem),
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.ComputationType)]: {
                    type: FieldType.ComboBox,
                    width: BasicInputSizes.XL,
                    isReadOnly: isNotOneTimeExtraSalary,
                    defaultValue: PrExtraSalaryComputationTypeCode.AverageHourlySalaryPercentage,
                    fieldSettings: {
                        displayName: PrExtraSalaryComputationTypeEntity.Name
                    },
                    isVisible: not(isPartOfLegislativeExtraSalaryItem),
                    affectedFields: [{
                        id: PrPayslipEmploymentExtraSalaryEntity.LengthOverride,
                        navigateFromParent: true
                    }]
                },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.LengthOverride)]: {
                ...getExtraSalaryFieldDefinition(PrPayslipEmploymentExtraSalaryEntity.LengthOverride),
                width: FastEntryInputSizes.XS,
                isRequired: true,
                affectedFields: [{
                    id: ExtraSalaryAmountPath,
                    navigateFromParent: true
                }, {
                    id: extraSalariesOverviewTablePath,
                    navigateFromParent: true
                }]
            },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, PrPayslipEmploymentExtraSalaryEntity.ValueOverride)]: {
                    type: FieldType.NumberInput,
                    width: BasicInputSizes.S,
                    label: i18next.t("Payslip:Form.ValueOverride"),
                    isRequired: not(isNotOneTimeExtraSalary),
                    isReadOnly: ifAny(isNotOneTimeExtraSalary, isPartOfLegislativeExtraSalaryItem),
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    fieldSettings: {
                        unit: getExtraSalaryValueUnit,
                        showSteppers: false,
                        min: 0
                    },
                    validator: {
                        type: ValidatorType.Number,
                        settings: {
                            min: 0
                        }
                    },
                    affectedFields: [{ id: ExtraSalaryAmountPath, navigateFromParent: true }]
                },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.ExtraSalaries, ExtraSalaryAmountPath)]: {
                ...getExtraSalaryFieldDefinition(ExtraSalaryAmountPath),
                width: BasicInputSizes.S,
                fieldSettings: {
                    unit: getPayrollCurrencyUnit(getPayslipCurrencyPath())
                }
            },
                // ---------- ExtraSalary fields ----------
                // ---------- Benefit fields ----------
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.Name)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Name),
                    isReadOnly: isNotOneTimeBenefit
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.Type)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Type),
                    isReadOnly: isNotOneTimeBenefit
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.OtherCategory)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.OtherCategory),
                    isReadOnly: isNotOneTimeBenefit
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability),
                    isReadOnly: isNotOneTimeBenefit
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.TaxDeducibility)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.TaxDeducibility),
                    isReadOnly: isNotOneTimeBenefit
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.AmountOverride)]: {
                    ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.Amount),
                    affectedFields: [
                        { id: BenefitTotalPath, navigateFromParent: true },
                    ]
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.AmountEmployeeOverride)]: {
                    ...AmountEmployeeDef,
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    fieldSettings: {
                        ...AmountEmployeeDef.fieldSettings,
                        unit: getMealVoucherShareUnitFormatter(PrPayslipEmploymentBenefitEntity.AmountOverride)
                    },
                    affectedFields: [
                        { id: BenefitEmployeeTotalPath, navigateFromParent: true }
                    ]
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.AmountEmployerOverride)]: {
                    ...AmountEmployerDef,
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    fieldSettings: {
                        ...AmountEmployerDef.fieldSettings,
                        unit: getMealVoucherShareUnitFormatter(PrPayslipEmploymentBenefitEntity.AmountOverride)
                    },
                    affectedFields: [
                        { id: BenefitEmployerTotalPath, navigateFromParent: true },
                    ]
                },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, PrPayslipEmploymentBenefitEntity.LengthOverride)]: {
                ...getBenefitFieldDefinition(PrPayslipEmploymentBenefitEntity.LengthOverride),
                width: BasicInputSizes.XS,
                isRequired: true,
                isVisible: not(isOtherBenefit),
                affectedFields: [
                    ...BenefitTotalFieldPaths.map(id => ({ id, navigateFromParent: true }))
                ]
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, BenefitEmployeeTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitEmployeeTotalPath),
                width: FastEntryInputSizes.S,
                textAlign: TextAlign.Right,
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, BenefitEmployerTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitEmployerTotalPath),
                width: FastEntryInputSizes.S,
                textAlign: TextAlign.Right,
            },
            [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Benefits, BenefitTotalPath)]: {
                ...getBenefitFieldDefinition(BenefitTotalPath),
                width: BasicInputSizes.S,
                textAlign: TextAlign.Right,
            },
                // ---------- /Benefit fields ----------
                // ---------- Retirement fields --------
                ...Object.fromEntries(
                    Object.entries(getRetirementSavingsBasicFields(PrPayslipEntity.Employments)).map(([key, def]) => [
                        key,
                        {
                            ...def,
                            isDisabled: isNotOneTimeRetirementSavings
                        }
                    ])
                ),
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.ComputationType)]: {
                    ...getRetirementSavingsComputationTypeDef(PrPayslipEmploymentRetirementSavingEntity.IsIncluded),
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.RetirementSavings, PrPayslipEmploymentRetirementSavingEntity.IsIncluded)]: {
                    ...getRetirementSavingsIsIncludedAutomaticallyDef(),
                },
                ...Object.fromEntries(
                    Object.entries(getRetirementSavingsSecondaryFields()).map(([key, value]) => [`${PrPayslipEntity.Employments}/${PrEmploymentEntity.RetirementSavings}/${key}Override`, value])
                ),
                // ---------- /Retirement fields
                // additional (unsorted) fields
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.AverageSalaryOverride)]: {
                    type: FieldType.NumberInput,
                    label: i18next.t("Payslip:Form.AverageSalaryOverride"),
                    width: BasicInputSizes.M,
                    isRequired: true,
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    isVisible: not(isStatutoryCompensationType)
                },
                [createPath(PrPayslipEntity.Employments, PrPayslipEmploymentEntity.Reduced60AverageSalary)]: {
                    isReadOnly: true,
                    ...getPayrollCurrencyFieldDef(getPayslipCurrencyPath()),
                    isVisible: not(isStatutoryCompensationType)
                }
            },
            groups: isCustomerOverview ? [
                // For Customer Overview has only calculation group visible
                calculationGroupDef
                // todo: if Attendance is not closed, group is not visible. Consider adding a placeholder with info.
            ] : [{
                ...calculationGroupDef,
                title: i18next.t("Payslip:Form.Calculations"),
        }, {
            id: "DeductionsProps",
            title: i18next.t("Payslip:Form.Deductions"),
            rows: [[
                { id: PrPayslipEntity.IsUnreducedMultipleDistrainmentOverride },
                { id: hasUnseizableOverridePath },
                { id: PrPayslipEntity.UnseizableTotalAmountOverride }
            ]]
        }, {
            id: "Deductions",
            title: i18next.t("Payslip:Form.DeductionsChanges"),
            rows: [
                [{ id: deductionsPath }]
            ]
        }, {
            id: "SalaryAdvances",
            title: i18next.t("Payslip:Form.SalaryAdvances"),
            rows: [],
            isVisible: ifAll(not(hasOpenAttendance), hasSalaryAdvances),
            lineItems: {
                collection: PrPayslipEntity.Items,
                filterItems: (_storage, _id, items: IEntity[]) => {
                    return (items as IPrPayslipItemEntity[])?.filter(item => item.Type?.Code === PrPayslipItemTypeCode.SalaryAdvance) ?? [];
                },
                columns: [
                    { id: PrPayslipItemEntity.SalaryAdvance },
                    { id: PrPayslipItemEntity.DecimalValue },
                    { id: createPath(PrPayslipItemEntity.Document, DocumentEntity.DocumentType) },
                    { id: PrPayslipItemEntity.Document },
                    { id: createPath(PrPayslipItemEntity.Document, DocumentEntity.ClearedStatus) },
                ],
                isItemRemovable: false,
                isItemCloneable: false,
                canReorder: false,
                canAdd: false,
                order: "Id"
            }
        }, {
            id: "LiabilitiesAndReceivables",
            title: i18next.t("Payslip:Form.LiabilitiesAndReceivables"),
            isVisible: hasLiabilitiesOrReceivables,
            lineItems: {
                collection: PrPayslipEntity.LiabilitiesAndReceivables,
                columns: [
                    { id: PrPayslipLiabilityReceivableEntity.Amount },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.Amount) },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document, DocumentEntity.DocumentType) },
                    { id: createPath(PrPayslipLiabilityReceivableEntity.Document) }
                ],
                isReadOnly: true,
                disableVirtualization: true,
                order: PrPayslipLiabilityReceivableEntity.DateCreated,
                canReorder: false,
                isItemCloneable: false,
                canAdd: false,
                isItemRemovable: true
            }
        }, {
            id: "socialInsuranceDiscount",
            title: i18next.t("Payslip:Form.EmployerSocialInsuranceDiscount"),
            togglePropPath: PrPayslipEntity.HasEmployerSocialInsuranceDiscountOverride,
            rows: [[{ id: SocialInsuranceDiscountTypeLocalPath }]],
            isVisible: not(hasOpenAttendance)
        }, {
            id: "HealthInsuranceBaseGroup",
            title: i18next.t("Payslip:Form.HealthInsuranceBaseGroupTitle"),
                isVisible: ifAll(not(hasOpenAttendance), not(isDelayedIncome), hasHealthInsuranceSurcharge),
            rows: [[{
                id: MinimumHealthInsuranceTypeOverviewTablePath
            }], [{
                id: PrPayslipEntity.IsDeduceMinimumHealthInsuranceOverride
            }], [{
                id: PrPayslipEntity.IsMinimumHealthInsurancePayedByEmployerOverride
            }]]
        }, {
            id: "EmploymentTabs",
            rows: [[{ id: tabsPath }]]
        }, {
            id: "Attendance",
            title: i18next.t("Payslip:Form.Attendance"),
            rows: [[{ id: attendanceOverviewPath }]],
            ...employmentsBaseWithoutVisibilityDef,
            isVisible: ifAll(isPayslipEmploymentVisible, not(isDelayedIncome))
        }, {
                id: "VacationOverview",
                title: i18next.t("Payslip:Form.VacationOverviewTitle"),
                rows: [[{ id: PrPayslipEmploymentEntity.VacationSummary }]],
                ...employmentsBaseWithoutVisibilityDef,
                isVisible: ifAll(employmentsBaseDef.isVisible, not(isDelayedIncome), not(isStatutoryCompensationType))
            }, {
            id: "Averages",
            title: i18next.t("Payslip:Form.Averages"),
            rows: [[{ id: PrPayslipEmploymentEntity.AverageSalaryOverride }, { id: PrPayslipEmploymentEntity.Reduced60AverageSalary }]],
            ...employmentsBaseDef,
            isVisible: ifAll(employmentsBaseDef.isVisible, not(isDelayedIncome))
        }, {
                id: salaryComponentsGroupId,
                title: i18next.t("Payslip:Form.SalaryComponents"),
                ...employmentsBaseDef,
                lineItems: {
                    collection: PrPayslipEmploymentEntity.SalaryComponents,
                    columns: [
                        { id: PrPayslipEmploymentSalaryComponentEntity.Name },
                        { id: PrPayslipEmploymentSalaryComponentEntity.Type },
                        { id: PrPayslipEmploymentSalaryComponentEntity.RegularSalaryType },
                        { id: PrPayslipEmploymentSalaryComponentEntity.IsBasicSalary },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusTaxImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusPayOutImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.FixedBonusDeductionImpact },
                        { id: PrPayslipEmploymentSalaryComponentEntity.TimeBasedBonusType },
                        { id: PrPayslipEmploymentSalaryComponentEntity.AverageInclusion },
                        { id: PrPayslipEmploymentSalaryComponentEntity.PercentageOverride },
                        { id: PrPayslipEmploymentSalaryComponentEntity.AmountOverride },
                    ],
                    additionalFields: [
                        { id: PrPayslipEmploymentSalaryComponentEntity.Amount },
                        { id: PrPayslipEmploymentSalaryComponentEntity.Percentage },
                    ],
                    useLabelWrapping: areAllSalaryComponentsOfSameType,
                    renderAsFastEntryForReadOnly: true,
                    disableVirtualization: true,
                    order: PrPayslipEmploymentSalaryComponentEntity.Id,
                    canReorder: false,
                    isItemCloneable: false,
                    isAddButtonTransparent: true,
                    customActionButtons: [{
                        id: DialogType.SalaryComponent,
                        title: i18next.t("Payslip:Form.Pick"),
                        position: ActionButtonPosition.Before,
                        isTransparent: false,
                        isVisible: not(isProcessedPayrollPeriod)
                    }]
                }
            }, {
                id: BenefitsGroupId,
                title: i18next.t("Payslip:Form.Benefits"),
                ...employmentsBaseDef,
                lineItems: {
                    collection: PrPayslipEmploymentEntity.Benefits,
                    columns: [
                        { id: PrPayslipEmploymentBenefitEntity.Name },
                        { id: PrPayslipEmploymentBenefitEntity.Type },
                        { id: PrPayslipEmploymentBenefitEntity.OtherCategory },
                        { id: PrPayslipEmploymentBenefitEntity.TaxAndContributionsApplicability },
                        { id: PrPayslipEmploymentBenefitEntity.TaxDeducibility },
                        { id: PrPayslipEmploymentBenefitEntity.AmountOverride },
                        { id: PrPayslipEmploymentBenefitEntity.AmountEmployerOverride },
                        { id: PrPayslipEmploymentBenefitEntity.AmountEmployeeOverride },
                        { id: PrPayslipEmploymentBenefitEntity.LengthOverride },
                        { id: BenefitTotalPath },
                        { id: BenefitEmployerTotalPath },
                        { id: BenefitEmployeeTotalPath },
                    ],
                    additionalFields: [
                        { id: PrPayslipEmploymentBenefitEntity.Length },
                        { id: PrPayslipEmploymentBenefitEntity.Amount },
                        { id: PrPayslipEmploymentBenefitEntity.AmountEmployer },
                        { id: PrPayslipEmploymentBenefitEntity.AmountEmployee },
                    ],
                    useLabelWrapping: areAllBenefitsOfSameType,
                    renderAsFastEntryForReadOnly: true,
                    disableVirtualization: true,
                    order: PrPayslipEmploymentBenefitEntity.Id,
                    canReorder: false,
                    isItemCloneable: false,
                    isTheOnlyItemRemovable: true,
                    isAddButtonTransparent: true,
                    customActionButtons: [{
                        id: DialogType.Benefits,
                        title: i18next.t("Payslip:Form.Pick"),
                        position: ActionButtonPosition.Before,
                        isTransparent: false,
                        isVisible: not(isProcessedPayrollPeriod)
                    }]
                },
                isVisible: ifAll(employmentsBaseDef.isVisible, not(isDelayedIncome))
            }, {
                id: BalancingPeriodGroupId,
                title: i18next.t("Payslip:Form.BalancingPeriod"),
                rows: [[{ id: balancingPeriodOverviewPath }]],
                ...employmentsBaseDef,
                isVisible: ifAll(employmentsBaseDef.isVisible, not(isDelayedIncome), hasBalancingPeriodOverview)
            }, {
                id: extraSalariesGroupId,
                title: i18next.t("Payslip:Form.ExtraSalaries"),
                rows: [
                    [{ id: extraSalariesOverviewTablePath }],
                    [{ id: LegislativeExtraSalaryLocalPath }],
                ],
                lineItems: {
                    collection: PrPayslipEmploymentEntity.ExtraSalaries,
                    columns: [
                        { id: PrPayslipEmploymentExtraSalaryEntity.Name },
                        { id: PrPayslipEmploymentExtraSalaryEntity.ComputationType },
                        { id: PrPayslipEmploymentExtraSalaryEntity.LengthOverride },
                        { id: PrPayslipEmploymentExtraSalaryEntity.ValueOverride },
                        { id: ExtraSalaryAmountPath }
                    ],
                    additionalFields: [
                        { id: PrPayslipEmploymentExtraSalaryEntity.Value },
                        { id: PrPayslipEmploymentExtraSalaryEntity.Length },
                        { id: PrPayslipEmploymentExtraSalaryEntity.TypeCode },
                        { id: createPath(PrPayslipEmploymentExtraSalaryEntity.Type, PrExtraSalaryTypeEntity.OriginCode) }
                    ],
                    disableVirtualization: true,
                    renderAsFastEntryForReadOnly: true,
                    order: PrPayslipEmploymentExtraSalaryEntity.Id,
                    canReorder: false,
                    isItemVisible: not(isLegislativeExtraSalary),
                    isItemCloneable: false,
                    isTheOnlyItemRemovable: true,
                    isAddButtonTransparent: true,
                    customActionButtons: [{
                        id: DialogType.ExtraSalary,
                        title: i18next.t("Payslip:Form.Pick"),
                        position: ActionButtonPosition.Before,
                        isTransparent: false,
                        isVisible: not(isProcessedPayrollPeriod)
                    }]
                },
                ...employmentsBaseDef,
                isVisible: ifAll(employmentsBaseDef.isVisible, not(isStatutoryCompensationType), not(isDelayedIncome))
            }, {
                id: RetirementsGroupId,
                ...employmentsBaseWithoutVisibilityDef,
                isVisible: ifAll(isPayslipEmploymentVisible, not(hasOpenAttendance), not(isDelayedIncome), ifAny(hasRetirementSavings, not(isFormReadOnly))),
                title: i18next.t("Payslip:Form.RetirementSavings"),
                lineItems: {
                    collection: PrPayslipEmploymentEntity.RetirementSavings,
                    disableVirtualization: true,
                    canAdd: true,
                    order: PrPayslipEmploymentRetirementSavingEntity.Id,
                    canReorder: false,
                    useLabelWrapping: areAllRetirementsOfSameType.bind(this, "ComputationTypeCode"),
                    renderAsFastEntryForReadOnly: true,
                    isItemCloneable: false,
                    isItemRemovable: (args: IGetValueArgs) => {
                        return args.storage.getValue(args.bindingContext)?.IsOneTime;
                    },
                    isTheOnlyItemRemovable: true,
                    additionalFields: [{ id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.PrRetirementSavingsCompany, PrRetirementSavingsCompanyEntity.Name) },
                        { id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.PrRetirementSavingsCompany, PrRetirementSavingsCompanyEntity.Id) }],
                    columns: [
                        { id: createPath(PrEmploymentRetirementSavingEntity.RetirementSavingsProduct, PrRetirementSavingsProductEntity.Type) },
                        { id: productPath },
                        { id: PrPayslipEmploymentRetirementSavingEntity.ComputationType },
                        { id: PrPayslipEmploymentRetirementSavingEntity.IsIncluded },
                        { id: PrPayslipEmploymentRetirementSavingEntity.AmountOverride },
                        { id: PrPayslipEmploymentRetirementSavingEntity.PercentageOverride },
                        { id: PrPayslipEmploymentRetirementSavingEntity.SymbolVariableOverride },
                        { id: PrPayslipEmploymentRetirementSavingEntity.SymbolSpecificOverride },
                        { id: PrPayslipEmploymentRetirementSavingEntity.SymbolConstantOverride },
                        { id: PrPayslipEmploymentRetirementSavingEntity.RemittanceInformationOverride },
                    ],
                }
            }]
        };

        return {
            entitySet: EntitySetName.PrPayslips,
            table,
            form
        };
    };

    getDefinitions.translationFiles = [
        "Payslip", "Common", "Document", "RetirementSavingsCompany",
        // selection tableViews
        "ExtraSalary", "SalaryComponent", "Benefit", "Attendance",
        ...getSalaryEnumNamespaceNames(),
        ...StatusCodeEnumTranslations,
        getEnumNameSpaceName(EntityTypeName.PrExtraSalaryComputationType),
        getEnumNameSpaceName(EntityTypeName.PrMinimumHealthInsuranceType),
        getEnumNameSpaceName(EntityTypeName.PrSocialInsuranceDiscountType),
        getEnumNameSpaceName(EntityTypeName.PrSocialInsuranceDiscountIneligibilityReason),
        getEnumNameSpaceName(EntityTypeName.DocumentType)
    ];

    return getDefinitions;
};

export const getDefinitions = getDefinitionsFactory();

setDefByEntityType(EntityTypeName.PrPayslip, getDefinitions);
